import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { NavBar } from '../components/NavBar'
import { supabase } from '../lib/supabase'
import { compressImage } from '../lib/imageCompression'
import { useAuth } from '../contexts/AuthContext'
import { usePlays } from '../hooks/usePlays'
import { useProductionsByPlay } from '../hooks/useProductions'
import { getLogEntryById, updateLogEntry } from '../hooks/useLogEntries'
import { ProductionType } from '../types'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { PhotoUpload } from '../components/PhotoUpload'
import { PlayCombobox } from '../components/PlayCombobox'
import { formatDateLong } from '../lib/dateUtils'

const PRODUCTION_TYPE_OPTIONS: { value: ProductionType; label: string }[] = [
  { value: 'live', label: 'Live Performance' },
  { value: 'filmed_stage', label: 'Filmed Stage Production' },
  { value: 'feature_film', label: 'Feature Film' },
  { value: 'television', label: 'Television' },
  { value: 'audio', label: 'Audio / Radio' },
  { value: 'immersive', label: 'Immersive / Site-Specific' },
  { value: 'adaptation', label: 'Adaptation' },
]

const LIVE_TYPES: ProductionType[] = ['live', 'immersive']

function getCompanyPlaceholder(type: ProductionType): string {
  return LIVE_TYPES.includes(type)
    ? 'e.g. Royal Shakespeare Company'
    : 'e.g. BBC'
}

function getVenuePlaceholder(type: ProductionType): string {
  return LIVE_TYPES.includes(type)
    ? 'e.g. Barbican Theatre'
    : 'e.g. BritBox, Globe Player'
}

const SELECT_CLASS =
  'w-full rounded-md border border-stone-700 bg-stone-800 px-3 py-2 text-sm text-stone-100 focus:outline-none focus:ring-1 focus:ring-red-800'

function todayLocalDate(): string {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}


function StarRating({
  value,
  onChange,
}: {
  value: number | null
  onChange: (rating: 1 | 2 | 3 | 4 | 5) => void
}) {
  const [hover, setHover] = useState<number | null>(null)
  const active = hover ?? value ?? 0

  return (
    <div className="flex gap-2" role="group" aria-label="Rating">
      {([1, 2, 3, 4, 5] as const).map((star) => {
        const filled = active >= star
        return (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(null)}
            aria-label={`${star} star${star !== 1 ? 's' : ''}`}
            className="transition-transform hover:scale-110 focus:outline-none"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-9 w-9"
              fill={filled ? '#b45309' : 'none'}
              stroke={filled ? '#b45309' : '#57534e'}
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z"
              />
            </svg>
          </button>
        )
      })}
    </div>
  )
}

export function LogEntryPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { plays, loading: playsLoading, error: playsError } = usePlays()

  const editId = searchParams.get('edit') ?? ''
  const isEditMode = !!editId
  const skipDuplicateCheck = searchParams.get('skip_duplicate_check') === '1'

  const [playId, setPlayId] = useState('')

  const { productions, loading: productionsLoading } = useProductionsByPlay(playId)
  const [productionId, setProductionId] = useState('')
  const [addingNewProduction, setAddingNewProduction] = useState(false)
  const [newProductionType, setNewProductionType] = useState<ProductionType>('live')
  const [newProductionCompany, setNewProductionCompany] = useState('')
  const [newProductionVenue, setNewProductionVenue] = useState('')

  const [dateWatched, setDateWatched] = useState(todayLocalDate)
  const [rating, setRating] = useState<1 | 2 | 3 | 4 | 5 | null>(null)
  const [reviewText, setReviewText] = useState('')

  const [existingPhotoUrl, setExistingPhotoUrl] = useState<string | null>(null)
  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const photoFileRef = useRef<File | null>(null)
  const [photoUploadError, setPhotoUploadError] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [editLoading, setEditLoading] = useState(isEditMode)
  const [duplicateDialog, setDuplicateDialog] = useState<{
    priorDate: string
    productionLabel: string
    resolvedProductionId: string
  } | null>(null)

  // Stable ref holds the production_id to pre-select once productions load.
  const preselectedProductionId = useRef(searchParams.get('production_id') ?? '')

  // On mount: if a production_id param is present, fetch that production to
  // resolve its play_id, then set the play — the productions effect below will
  // complete the selection once the productions list loads.
  useEffect(() => {
    const id = preselectedProductionId.current
    if (!id || isEditMode) return

    async function resolvePlay() {
      const { data } = await supabase
        .from('productions')
        .select('id, play_id')
        .eq('id', id)
        .maybeSingle()

      if (data?.play_id) {
        setPlayId(data.play_id)
      }
    }

    resolvePlay()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // In edit mode, load the existing entry and pre-populate all fields.
  useEffect(() => {
    if (!editId) return

    async function loadEntry() {
      const { data } = await getLogEntryById(editId)
      if (!data) {
        setEditLoading(false)
        return
      }
      setDateWatched(data.date_watched)
      setRating(data.rating)
      setReviewText(data.review_text ?? '')
      setExistingPhotoUrl(data.user_photo_url)
      // Store production id; the productions-load effect applies it once the
      // productions list arrives.
      preselectedProductionId.current = data.production_id
      setPlayId(data.productions.plays.id)
      setEditLoading(false)
    }

    loadEntry()
  }, [editId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Clear production when the play changes so a stale selection isn't shown.
  // This must be defined BEFORE the production-selection effect below so that
  // React fires it first — the selection effect then overwrites it if there is
  // a preselected production id to restore (edit mode / ?production_id= param).
  useEffect(() => {
    setProductionId('')
    setAddingNewProduction(false)
    setNewProductionCompany('')
    setNewProductionVenue('')
  }, [playId])

  // When productions finish loading, apply the preselected production id if
  // it is present in the loaded list.
  useEffect(() => {
    const id = preselectedProductionId.current
    if (!id || productionsLoading) return

    if (productions.some((p) => p.id === id)) {
      setProductionId(id)
      preselectedProductionId.current = ''
    }
  }, [productions, productionsLoading])

  function handleProductionChange(value: string) {
    if (value === '__new__') {
      setAddingNewProduction(true)
      setProductionId('')
    } else {
      setAddingNewProduction(false)
      setProductionId(value)
    }
  }

  // Computed label for a production in the dropdown: prefer company/studio,
  // fall back to legacy title, then to the play name for new productions.
  function productionOptionLabel(prod: { title: string | null; company_or_studio: string | null; year: number | null }, playTitle: string): string {
    const label = prod.company_or_studio ?? prod.title ?? playTitle
    return prod.year ? `${label} (${prod.year})` : label
  }

  async function doInsert(resolvedProductionId: string) {
    const { data: newEntry, error: logError } = await supabase
      .from('log_entries')
      .insert({
        user_id: user?.id,
        production_id: resolvedProductionId,
        date_watched: dateWatched,
        rating,
        review_text: reviewText.trim() || null,
        user_photo_url: null,
      })
      .select('id')
      .single()

    if (logError || !newEntry) {
      setError(logError?.message ?? 'Failed to save log entry.')
      setSubmitting(false)
      return
    }

    if (user && resolvedProductionId) {
      await supabase
        .from('want_to_watch')
        .delete()
        .eq('user_id', user.id)
        .eq('production_id', resolvedProductionId)
    }

    const pendingPhoto = photoFileRef.current

    if (pendingPhoto && user) {
      setIsUploading(true)
      setPhotoUploadError(null)
      try {
        const compressed = await compressImage(pendingPhoto)
        const ext = compressed.name.split('.').pop() ?? 'jpg'
        const path = `${user.id}/${newEntry.id}/${Date.now()}.${ext}`

        const { error: uploadError } = await supabase.storage
          .from('performance-photos')
          .upload(path, compressed, { upsert: false })

        if (uploadError) {
          setPhotoUploadError(
            'Your entry was saved, but the photo upload failed. You can try adding the photo later.'
          )
        } else {
          const { data: urlData } = supabase.storage
            .from('performance-photos')
            .getPublicUrl(path)

          await supabase
            .from('log_entries')
            .update({ user_photo_url: urlData.publicUrl })
            .eq('id', newEntry.id)
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Photo upload failed.'
        setPhotoUploadError(
          `Your entry was saved, but the photo could not be uploaded: ${message}`
        )
      }
      setIsUploading(false)
    }

    setSubmitting(false)
    navigate('/profile')
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)

    if (!playId) {
      setError('Please select a play.')
      setSubmitting(false)
      return
    }
    if (!addingNewProduction && !productionId) {
      setError('Please select a production, or choose "Add a new production".')
      setSubmitting(false)
      return
    }
    if (!dateWatched) {
      setError('Please enter the date you watched this.')
      setSubmitting(false)
      return
    }

    let resolvedProductionId = productionId

    if (addingNewProduction) {
      const { data: newProd, error: prodError } = await supabase
        .from('productions')
        .insert({
          play_id: playId,
          type: newProductionType,
          company_or_studio: newProductionCompany.trim() || null,
          venue_or_platform: newProductionVenue.trim() || null,
          created_by_user_id: user?.id ?? null,
        })
        .select('id')
        .single()

      if (prodError || !newProd) {
        setError(prodError?.message ?? 'Failed to create production.')
        setSubmitting(false)
        return
      }

      resolvedProductionId = newProd.id
    }

    if (isEditMode) {
      let photoUrl: string | null = existingPhotoUrl

      const pendingPhoto = photoFileRef.current
      if (pendingPhoto && user) {
        setIsUploading(true)
        setPhotoUploadError(null)
        try {
          const compressed = await compressImage(pendingPhoto)
          const ext = compressed.name.split('.').pop() ?? 'jpg'
          const path = `${user.id}/${editId}/${Date.now()}.${ext}`

          const { error: uploadError } = await supabase.storage
            .from('performance-photos')
            .upload(path, compressed, { upsert: false })

          if (uploadError) {
            setPhotoUploadError(
              'Your entry was saved, but the photo upload failed. You can try again later.'
            )
          } else {
            const { data: urlData } = supabase.storage
              .from('performance-photos')
              .getPublicUrl(path)
            photoUrl = urlData.publicUrl
          }
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : 'Photo upload failed.'
          setPhotoUploadError(
            `Your entry was saved, but the photo could not be uploaded: ${message}`
          )
        }
        setIsUploading(false)
      }

      const { error: updateError } = await updateLogEntry(editId, {
        production_id: resolvedProductionId,
        date_watched: dateWatched,
        rating: rating,
        review_text: reviewText.trim() || null,
        user_photo_url: photoUrl,
      })

      setSubmitting(false)

      if (updateError) {
        setError(updateError)
        return
      }

      navigate('/diary')
      return
    }

    if (!skipDuplicateCheck) {
      const { data: existing } = await supabase
        .from('log_entries')
        .select('date_watched')
        .eq('user_id', user?.id)
        .eq('production_id', resolvedProductionId)
        .order('date_watched', { ascending: false })
        .limit(1)

      if (existing && existing.length > 0) {
        const prod = productions.find((p) => p.id === resolvedProductionId)
        const productionLabel = prod
          ? (prod.company_or_studio ?? prod.title ?? selectedPlay?.title ?? 'this production')
          : (selectedPlay?.title ?? 'this production')
        setDuplicateDialog({
          priorDate: formatDateLong(existing[0].date_watched),
          productionLabel,
          resolvedProductionId,
        })
        setSubmitting(false)
        return
      }
    }

    await doInsert(resolvedProductionId)
  }

  function handleConfirmDuplicate() {
    if (!duplicateDialog) return
    const { resolvedProductionId } = duplicateDialog
    setDuplicateDialog(null)
    setSubmitting(true)
    doInsert(resolvedProductionId)
  }

  function handleCancelDuplicate() {
    setDuplicateDialog(null)
    setSubmitting(false)
  }

  const selectedPlay = plays.find((p) => p.id === playId)

  return (
    <div className="min-h-screen bg-stone-900 text-stone-100">
      {duplicateDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={handleCancelDuplicate}
          />
          <div className="relative z-10 mx-4 max-w-sm rounded-lg border border-stone-700 bg-stone-900 p-6 shadow-xl">
            <h2 className="mb-3 font-serif text-lg font-semibold text-stone-100">
              Already logged
            </h2>
            <p className="mb-6 text-sm text-stone-300">
              You already logged{' '}
              <span className="font-medium text-stone-100">
                {duplicateDialog.productionLabel}
              </span>{' '}
              on {duplicateDialog.priorDate}. Log it again?
            </p>
            <div className="flex gap-3">
              <Button
                type="button"
                onClick={handleConfirmDuplicate}
                className="flex-1 bg-red-900 text-stone-100 hover:bg-red-800"
              >
                Log it again
              </Button>
              <Button
                type="button"
                onClick={handleCancelDuplicate}
                className="flex-1 border border-stone-600 bg-transparent text-stone-300 hover:bg-stone-800"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      <NavBar />
      <div className="px-6 py-10">
      <div className="mx-auto max-w-xl">
        <h1 className="mb-1 font-serif text-3xl font-semibold tracking-tight">
          {isEditMode ? 'Edit Entry' : 'Log a Performance'}
        </h1>
        <p className="mb-8 text-sm text-stone-400">
          {isEditMode
            ? 'Update your log entry.'
            : 'Record a Shakespeare performance you have seen.'}
        </p>

        {editLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 rounded bg-stone-800" />
            ))}
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-7">

          {/* Play */}
          <div className="space-y-2">
            <Label className="text-stone-300 font-medium">Play</Label>
            {playsError ? (
              <p className="text-sm text-red-400">Failed to load plays: {playsError}</p>
            ) : playsLoading ? (
              <p className="text-sm text-stone-400">Loading plays…</p>
            ) : (
              <PlayCombobox
                plays={plays}
                value={playId}
                onChange={setPlayId}
              />
            )}
          </div>

          {/* Date watched */}
          <div className="space-y-1.5">
            <Label htmlFor="date-watched" className="text-stone-300 font-medium">
              Date watched
            </Label>
            <Input
              id="date-watched"
              type="date"
              value={dateWatched}
              onChange={(e) => setDateWatched(e.target.value)}
              className="border-stone-700 bg-stone-800 text-stone-100"
            />
          </div>

          {/* Production — appears once a play is chosen */}
          {playId && (
            <div className="space-y-2">
              <Label htmlFor="production-select" className="text-stone-300 font-medium">Production</Label>
              {productionsLoading ? (
                <p className="text-sm text-stone-400">Loading productions…</p>
              ) : (
                <select
                  id="production-select"
                  value={addingNewProduction ? '__new__' : productionId}
                  onChange={(e) => handleProductionChange(e.target.value)}
                  className={SELECT_CLASS}
                >
                  <option value="">Select a production…</option>
                  {productions.map((prod) => (
                    <option key={prod.id} value={prod.id}>
                      {productionOptionLabel(prod, selectedPlay?.title ?? '')}
                    </option>
                  ))}
                  <option value="__new__">＋ Add a new production</option>
                </select>
              )}

              {addingNewProduction && (
                <div className="rounded-md border border-stone-700 bg-stone-800/40 p-4 space-y-3">
                  <p className="text-xs font-medium uppercase tracking-wider text-stone-500">
                    New production
                  </p>
                  <div className="space-y-1.5">
                    <Label htmlFor="new-prod-type" className="text-stone-300 text-sm">
                      Type
                    </Label>
                    <select
                      id="new-prod-type"
                      value={newProductionType}
                      onChange={(e) => setNewProductionType(e.target.value as ProductionType)}
                      className={SELECT_CLASS}
                    >
                      {PRODUCTION_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="new-prod-company" className="text-stone-300 text-sm">
                      Company / Studio{' '}
                      <span className="font-normal text-stone-500">(optional)</span>
                    </Label>
                    <Input
                      id="new-prod-company"
                      value={newProductionCompany}
                      onChange={(e) => setNewProductionCompany(e.target.value)}
                      placeholder={getCompanyPlaceholder(newProductionType)}
                      className="border-stone-700 bg-stone-800 text-stone-100 placeholder:text-stone-500"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="new-prod-venue" className="text-stone-300 text-sm">
                      Venue / Platform{' '}
                      <span className="font-normal text-stone-500">(optional)</span>
                    </Label>
                    <Input
                      id="new-prod-venue"
                      value={newProductionVenue}
                      onChange={(e) => setNewProductionVenue(e.target.value)}
                      placeholder={getVenuePlaceholder(newProductionType)}
                      className="border-stone-700 bg-stone-800 text-stone-100 placeholder:text-stone-500"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Rating */}
          <div className="space-y-2">
            <Label className="text-stone-300 font-medium">
              Rating{' '}
              <span className="font-normal text-stone-500">(optional)</span>
            </Label>
            <StarRating value={rating} onChange={setRating} />
            <p className="text-xs text-stone-500 h-4">
              {rating ? `${rating} star${rating !== 1 ? 's' : ''}` : 'Click a star to rate'}
            </p>
          </div>

          {/* Review text */}
          <div className="space-y-1.5">
            <Label htmlFor="review" className="text-stone-300 font-medium">
              Review{' '}
              <span className="font-normal text-stone-500">(optional)</span>
            </Label>
            <textarea
              id="review"
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="What stayed with you?"
              rows={4}
              className="w-full resize-none rounded-md border border-stone-700 bg-stone-800 px-3 py-2 text-sm text-stone-100 placeholder:text-stone-500 focus:outline-none focus:ring-1 focus:ring-red-800"
            />
          </div>

          {/* Existing photo in edit mode */}
          {isEditMode && existingPhotoUrl && !photoFile && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-stone-300">Current photo</p>
              <div className="relative w-full overflow-hidden rounded-md border border-stone-700">
                <img
                  src={existingPhotoUrl}
                  alt="Current performance photo"
                  className="max-h-64 w-full object-contain bg-stone-900"
                />
                <button
                  type="button"
                  onClick={() => setExistingPhotoUrl(null)}
                  className="absolute right-2 top-2 rounded-full bg-stone-900/80 p-1.5 text-stone-300 transition-colors hover:text-stone-100"
                  aria-label="Remove current photo"
                >
                  <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                    <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                  </svg>
                </button>
              </div>
              <p className="text-xs text-stone-500">Upload a new photo below to replace this one.</p>
            </div>
          )}

          {/* Photo upload */}
          {photoFile && <span data-testid="photo-file-ready" aria-hidden="true" />}
          <PhotoUpload
            onFileChange={(file) => {
              photoFileRef.current = file
              setPhotoFile(file)
            }}
            isUploading={isUploading}
            error={photoUploadError}
          />

          {error && (
            <p className="rounded-md bg-red-950 px-4 py-3 text-sm text-red-400">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-red-900 py-3 text-base font-semibold text-stone-100 hover:bg-red-800 disabled:opacity-60"
          >
            {submitting
              ? isEditMode ? 'Saving…' : 'Logging…'
              : isEditMode ? 'Save changes' : 'Log it'}
          </Button>
        </form>
        )}
      </div>
      </div>
    </div>
  )
}
